import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { API, api } from "@/lib/api";

const AuthContext = createContext(null);
const IDLE_MS = 10 * 60 * 1000;
const LAST_KEY = "disha_last_activity";
const EVENTS = ["mousemove", "mousedown", "keydown", "scroll", "touchstart"];

export const homeFor = (user) => (user && ["admin", "superadmin"].includes(user.role) ? "/admin" : "/dashboard");

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const lastWrite = useRef(0);

  const markActive = useCallback(() => {
    const t = Date.now();
    if (t - lastWrite.current > 10000) {
      lastWrite.current = t;
      localStorage.setItem(LAST_KEY, String(t));
    }
  }, []);

  const logout = useCallback(async (reason) => {
    try { await api.post("/auth/logout"); } catch (e) { /* already signed out */ }
    localStorage.removeItem(LAST_KEY);
    setUser(false);
    if (reason === "idle") toast.info("You were signed out after 10 minutes of inactivity. Your progress is saved.");
  }, []);

  useEffect(() => {
    const init = async () => {
      const last = Number(localStorage.getItem(LAST_KEY) || 0);
      if (last && Date.now() - last > IDLE_MS) {
        await logout();
        return;
      }
      try {
        const { data } = await api.get("/auth/me");
        setUser(data);
      } catch (e) {
        try {
          const { data } = await api.post("/auth/refresh");
          setUser(data);
        } catch (err) {
          setUser(false);
        }
      }
    };
    init();
    const onExpired = () => setUser(false);
    window.addEventListener("disha:session-expired", onExpired);
    return () => window.removeEventListener("disha:session-expired", onExpired);
  }, [logout]);

  useEffect(() => {
    if (!user) return undefined;
    localStorage.setItem(LAST_KEY, String(Date.now()));
    EVENTS.forEach((ev) => window.addEventListener(ev, markActive, { passive: true }));
    let lastBeat = Date.now();
    const timer = setInterval(() => {
      const last = Number(localStorage.getItem(LAST_KEY) || Date.now());
      if (Date.now() - last > IDLE_MS) return logout("idle");
      if (Date.now() - lastBeat > 60000) { lastBeat = Date.now(); api.post("/auth/heartbeat").catch(() => {}); }
    }, 20000);
    const onClose = () => navigator.sendBeacon?.(`${API}/auth/page-closed`);
    window.addEventListener("pagehide", onClose);
    return () => {
      EVENTS.forEach((ev) => window.removeEventListener(ev, markActive));
      window.removeEventListener("pagehide", onClose);
      clearInterval(timer);
    };
  }, [user, markActive, logout]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setUser(data);
    return data;
  };

  const signup = async (payload) => {
    const { data } = await api.post("/auth/signup", payload);
    setUser(data);
    return data;
  };

  const refreshUser = async () => {
    const { data } = await api.get("/auth/me");
    setUser(data);
    return data;
  };

  return <AuthContext.Provider value={{ user, login, signup, logout, refreshUser }}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
